/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Button, Sidebar, Label, Input, Description } from "@nordcloud/gnui";
import { Fieldset } from "~/components/Fieldset";
import { ContactsInput } from "~/components/Inputs";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showSuccess, showError } from "~/services/toast";
import { SelectTypeForm } from "./OrganizationUnit/Edit/SelectTypeForm";

export type OrganizationUnitFormData = {
  contactId?: string;
  typeId: string;
  name: string;
};

type Props = {
  editMode: boolean;
  isOpen: boolean;
  onSubmit: SubmitHandler<OrganizationUnitFormData>;
  onClose: () => void;
  initialValues?: OrganizationUnitFormData;
};

export function OrganizationUnitForm({
  editMode,
  isOpen,
  onClose,
  onSubmit,
  initialValues,
}: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm();

  React.useEffect(() => {
    register("name", { required: true });
    register("contactId");
    register("typeId");
  }, [register]);

  React.useEffect(() => {
    setValue("name", initialValues?.name);
    if (initialValues?.typeId !== "-") {
      setValue("typeId", initialValues?.typeId);
    }
    if (initialValues?.contactId !== "-") {
      setValue("contactId", initialValues?.contactId);
    }
  }, [initialValues, setValue]);

  React.useEffect(() => {
    reset();
  }, [isSubmitSuccessful]);

  const watcher = watch();

  return (
    <Sidebar
      isOpen={isOpen}
      title={
        editMode ? "Edit Organizational Unit" : "Add New Organizational Unit"
      }
      onClick={onClose}
    >
      <form
        data-testid="organization-unit-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Fieldset>
          <Label
            required
            htmlFor="input-name"
            name="Organizational Unit Name"
          />
          <Input
            id="input-name"
            type="text"
            {...register("name", { required: true })}
            status={errors.name && "error"}
            placeholder="Organizational Unit Name"
          />
          {errors.name && <Description>Name is required.</Description>}
        </Fieldset>
        <Fieldset>
          <Label htmlFor="input-contact" name="Contact Person" />
          <ContactsInput
            id="input-contact"
            placeholder="Contact Person"
            value={[watcher.contactId]}
            status={errors.contactId && "error"}
            onChange={(index: string) => {
              try {
                setValue("contactId", index);
                showSuccess(SUCCESS_TEXT.contactPersonAdded);
              } catch {
                showError(ERROR_TEXT.default);
              }
            }}
          />
        </Fieldset>
        <Fieldset>
          <SelectTypeForm
            typeId={watcher.typeId}
            onSubmit={(newTypeId) => setValue("typeId", newTypeId)}
          />
        </Fieldset>
        <Button type="submit" mt="2rem">
          {editMode
            ? "Update Organizational Unit"
            : "Add new Organizational Unit"}
        </Button>
      </form>
    </Sidebar>
  );
}
